import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { useEffect, useState } from "react";

import { whatIfSetup } from "@/libs/tournamentSetupApi";
import type { MatrixCell, QuickStart, SetupWhatIfResult, TournamentSetupView } from "@/libs/tournamentSetupApi";

import { cellKey, EventsMatrix, KINDS } from "../setup/events-matrix";
import { formatDay, PickerField } from "../setup/fields";
import { bandBar, bandText, DenseButton, DenseSelect, FieldLabel, Mono, Note, Segmented, SetupSurface, tone } from "../setup/ui";
import { useSetupCommand } from "./use-setup-command";

const QUICK: { value: QuickStart; label: string }[] = [
  { value: "one_doubles", label: "One doubles event" },
  { value: "club_standard", label: "Club standard" },
  { value: "custom", label: "Custom" },
];
const LEVEL_SETS = { AB: ["A", "B"], ABC: ["A", "B", "C"] } as const;

function quickCells(value: QuickStart): MatrixCell[] {
  if (value === "one_doubles") return [{ kind: "XD", level: "open", age_group: null }];
  if (value === "club_standard") {
    return (["open", "B"] as const).flatMap((level) => KINDS.map((k) => ({ kind: k.kind, level, age_group: null })));
  }
  return [];
}

function LegendTick({ has, children }: { has?: boolean; children: string }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <Box
        aria-hidden
        component="span"
        sx={{
          width: 16,
          height: 16,
          borderRadius: "4px",
          display: "inline-grid",
          placeItems: "center",
          border: `1px solid ${has ? tone.line : tone.brand}`,
          bgcolor: has ? tone.chip : tone.brand,
          color: has ? tone.muted : tone.surface,
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        ✓
      </Box>
      {children}
    </Box>
  );
}

/** Add events (F6): matrix with the events already in, an optional second
 * axis, a day and "age as of" for new events, and the server's sizing note. */
export function AddEventsDialog({
  view,
  open,
  onClose,
  onViewChange,
}: {
  view: TournamentSetupView;
  open: boolean;
  onClose: () => void;
  onViewChange: (next: TournamentSetupView) => void;
}) {
  const existing = view.events.map((event) => event.cell);
  const existingKeys = new Set(existing.map(cellKey));
  const [quick, setQuick] = useState<QuickStart>("custom");
  const [cells, setCells] = useState<MatrixCell[]>([]);
  const [secondAxis, setSecondAxis] = useState(true);
  const [levels, setLevels] = useState<keyof typeof LEVEL_SETS>("AB");
  const [day, setDay] = useState<string>("");
  const [ageAsOf, setAgeAsOf] = useState(view.days[0]?.play_date ?? "");
  const [preview, setPreview] = useState<SetupWhatIfResult | null>(null);
  const { pending, error, run } = useSetupCommand(view, onViewChange);
  const adding = cells.filter((cell) => !existingKeys.has(cellKey(cell)));
  const key = JSON.stringify([adding, day]);

  useEffect(() => {
    if (!open || !adding.length) {
      setPreview(null);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      void whatIfSetup(view.tournament_id, { add_events: adding, add_stage_day: day || null, changes: [] })
        .then((result) => {
          if (!active) return;
          setPreview(result);
          const chosen = result.add_events?.events[0]?.stage_days[0]?.play_date;
          if (!day && chosen) setDay(chosen);
        })
        .catch(() => {
          if (active) setPreview(null);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key]);

  const toggle = (cell: MatrixCell) => {
    const k = cellKey(cell);
    setQuick("custom");
    setCells((current) => (current.some((c) => cellKey(c) === k) ? current.filter((c) => cellKey(c) !== k) : [...current, cell]));
  };
  const add = preview?.add_events;
  const hasAgeGroups = adding.some((cell) => cell.age_group);
  const submit = async () => {
    if (!add) return;
    const drafts = add.events.map((draft) => ({
      ...draft,
      stage_days: day ? [{ stage: "all" as const, play_date: day }] : draft.stage_days,
      rules: { ...draft.rules, age_as_of: draft.cell.age_group ? ageAsOf || null : null },
    }));
    if (await run({ command: "add_events", events: drafts } as never)) {
      setCells([]);
      onClose();
    }
  };

  return (
    <Dialog aria-labelledby="mx-t" maxWidth={false} onClose={onClose} open={open} slotProps={{ paper: { sx: { width: 1140, maxWidth: "calc(100% - 32px)", borderRadius: "12px", mt: "40px" } } }} sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}>
      <SetupSurface>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "14px 18px", borderBottom: `1px solid ${tone.divider}` }}>
          <Box component="b" id="mx-t" sx={{ fontSize: 15 }}>
            Add events
          </Box>
          <Box component="span" sx={{ fontSize: 12, color: tone.subtle }}>
            Disciplines down the side, divisions across. Tick a box for each event.
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Segmented
            label="Quick start"
            onChange={(value) => {
              setQuick(value);
              if (value !== "custom") setCells(quickCells(value).filter((cell) => !existingKeys.has(cellKey(cell))));
            }}
            options={QUICK}
            value={quick}
          />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 330px" } }}>
          <Box sx={{ p: "14px 18px", borderRight: `1px solid ${tone.divider}`, overflowX: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "10px" }}>
              <Box component="label" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: 13, fontWeight: 600 }}>
                <input checked={secondAxis} onChange={(e) => setSecondAxis(e.target.checked)} style={{ accentColor: tone.brand, width: 15, height: 15, margin: 0 }} type="checkbox" /> Second axis
              </Box>
              {secondAxis ? (
                <>
                  <Box component="span" sx={{ fontSize: 12.5, color: tone.muted }}>
                    Combine
                  </Box>
                  <Box component="b" sx={{ fontSize: 12.5 }}>
                    age groups
                  </Box>
                  <Box component="span" sx={{ fontSize: 12.5, color: tone.muted }}>
                    with
                  </Box>
                  <DenseSelect aria-label="Second axis" onChange={(e) => setLevels(e.target.value as keyof typeof LEVEL_SETS)} sx={{ height: 28, width: "auto" }} value={levels}>
                    <option value="AB">Levels A, B</option>
                    <option value="ABC">Levels A, B, C</option>
                  </DenseSelect>
                  <Box component="span" sx={{ fontSize: 12, color: tone.subtle }}>
                    so a box can be “U15 B”
                  </Box>
                </>
              ) : null}
            </Box>
            <EventsMatrix
              existing={existing}
              onToggle={toggle}
              secondAxis={secondAxis ? { ageGroups: ["U13", "U15", "U17"], levels: [...LEVEL_SETS[levels]] } : null}
              selected={cells}
            />
            <Box sx={{ display: "flex", gap: 2, mt: "10px", fontSize: 12, color: tone.subtle, alignItems: "center", flexWrap: "wrap" }}>
              <LegendTick has>Already in this tournament</LegendTick>
              <LegendTick>Adding now</LegendTick>
              <span>Junior mixed (for example U15 B mixed) works like any other box.</span>
            </Box>
          </Box>
          <Box sx={{ p: "14px 18px", display: "flex", flexDirection: "column", gap: 1.5 }}>
            <div>
              <b>
                Adding {adding.length} event{adding.length === 1 ? "" : "s"}
              </b>
              <Box sx={{ fontSize: 12.5, color: tone.ink2, mt: "4px", lineHeight: 1.6 }}>
                {(add?.lines ?? []).map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </Box>
            </div>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <Box>
                <FieldLabel htmlFor="m-day">Day</FieldLabel>
                <DenseSelect id="m-day" onChange={(e) => setDay(e.target.value)} value={day}>
                  {!day ? <option value="">The emptier day</option> : null}
                  {view.days.map((d) => (
                    <option key={d.play_date} value={d.play_date}>
                      {formatDay(d.play_date)}
                    </option>
                  ))}
                </DenseSelect>
              </Box>
              {hasAgeGroups ? (
                <Box>
                  <FieldLabel>Age as of</FieldLabel>
                  <PickerField display={ageAsOf ? formatDay(ageAsOf, true) : "Pick a date"} label="Age as of" onChange={setAgeAsOf} type="date" value={ageAsOf} />
                </Box>
              ) : null}
            </Box>
            {add?.note ? <Note tone={add.note_band === "plenty" ? "green" : add.note_band === "wont_fit" ? "red" : "amber"}>{add.note}</Note> : null}
            {add?.day_label && add.after_band ? (
              <div>
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                  <b>{add.day_label}</b>
                  <Mono sx={{ fontWeight: 700, color: bandText[add.after_band] }}>
                    {add.before_percent_label}% → {add.after_percent_label}%
                  </Mono>
                </Box>
                <Box aria-hidden sx={{ height: 8, borderRadius: 4, bgcolor: tone.chip, overflow: "hidden", mt: "4px", position: "relative" }}>
                  <Box sx={{ position: "absolute", inset: 0, width: `${Math.min(100, Number.parseFloat(add.after_percent_label ?? "0"))}%`, bgcolor: tone.warnLine }} />
                  <Box sx={{ position: "absolute", inset: 0, width: `${Math.min(100, Number.parseFloat(add.before_percent_label ?? "0"))}%`, bgcolor: bandBar.plenty }} />
                </Box>
                <Box sx={{ fontSize: 11.5, mt: "3px", color: bandText[add.after_band] }}>{add.after_line}</Box>
              </div>
            ) : null}
            {add?.junior_note ? (
              <Box sx={{ fontSize: 12, color: tone.muted, lineHeight: 1.5, borderTop: `1px solid ${tone.divider}`, pt: "10px" }}>{add.junior_note}</Box>
            ) : null}
            {error ? (
              <Note role="alert" tone="red">
                {error}
              </Note>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: "12px 18px", borderTop: `1px solid ${tone.divider}`, bgcolor: tone.page }}>
          <Box component="span" sx={{ fontSize: 12, color: tone.subtle }}>
            Club standard = all 5 disciplines at Open and B. One doubles event = Mixed doubles · Open.
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <DenseButton onClick={onClose}>Cancel</DenseButton>
          <DenseButton disabled={pending || !add || !adding.length} onClick={submit} variant="primary">
            Add {adding.length} event{adding.length === 1 ? "" : "s"}
          </DenseButton>
        </Box>
      </SetupSurface>
    </Dialog>
  );
}
